import { icon } from './icons.js';

function element(tagName, options = {}, children = []) {
  const node = document.createElement(tagName);
  if (options.className) {
    node.className = options.className;
  }
  if (options.text !== undefined) {
    node.textContent = options.text;
  }
  for (const [name, value] of Object.entries(options.attributes || {})) {
    if (value !== null && value !== undefined) {
      node.setAttribute(name, String(value));
    }
  }
  for (const child of children) {
    if (child) {
      node.append(child);
    }
  }
  return node;
}

function brand({ title = 'Operations Leadership', subtitle = 'Private candidate portal' } = {}) {
  const logo = element('img', {
    className: 'brand__logo',
    attributes: { src: '/pub-invest-group.png', alt: 'Pub Invest Group' },
  });
  const divider = element('span', {
    className: 'brand__divider',
    attributes: { 'aria-hidden': 'true' },
  });
  const product = element('div', { className: 'brand__product' }, [
    element('strong', { text: title }),
    subtitle ? element('span', { text: subtitle }) : null,
  ]);
  return element('header', { className: 'brand', attributes: { 'aria-label': `Pub Invest ${title}` } }, [
    logo,
    divider,
    product,
  ]);
}

function statusRegion(message = '') {
  return element('div', {
    className: 'sr-only',
    text: message,
    attributes: {
      id: 'portal-status',
      role: 'status',
      'aria-live': 'polite',
      'aria-atomic': 'true',
    },
  });
}

function shell(content, statusMessage = '', {
  brandOptions,
  footerText = 'PRIVATE INVITATION · PUB INVEST',
} = {}) {
  return element('div', { className: 'portal-shell' }, [
    element('div', { className: 'portal-shell__inner' }, [
      brand(brandOptions),
      content,
      footerText ? element('footer', { className: 'portal-footer', text: footerText }) : null,
    ]),
    statusRegion(statusMessage),
  ]);
}

function viewFrame(iconName, content, tone = 'neutral') {
  const iconWrap = element('div', {
    className: `view-icon view-icon--${tone}`,
    attributes: { 'aria-hidden': 'true' },
  }, [icon(iconName, iconName === 'loading' ? 'icon icon--spin' : 'icon')]);

  return element('section', {
    className: `portal-view portal-view--${tone}`,
    attributes: { 'data-view': '', 'aria-labelledby': 'view-title' },
  }, [iconWrap, ...content]);
}

function errorAlert(message) {
  if (!message) {
    return null;
  }

  return element('div', {
    className: 'form-alert',
    text: message,
    attributes: {
      id: 'code-error',
      role: 'alert',
      tabindex: '-1',
    },
  });
}

function primaryButton(label, iconName, attributes = {}) {
  return element('button', {
    className: 'button button--primary',
    attributes: { type: 'submit', ...attributes },
  }, [icon(iconName), element('span', { text: label })]);
}

export function entryView({ code = '', error = '', busy = false } = {}) {
  const heading = element('h1', {
    className: 'view-title',
    text: 'Your invitation starts here.',
    attributes: { id: 'view-title' },
  });
  const intro = element('p', {
    className: 'view-intro',
    text: 'Enter your invitation code to continue.',
  });
  const label = element('label', {
    className: 'field-label',
    text: 'Invitation code',
    attributes: { for: 'invitation-code' },
  });
  const input = element('input', {
    className: 'code-input',
    attributes: {
      id: 'invitation-code',
      name: 'invitation-code',
      type: 'text',
      value: code,
      maxlength: '64',
      autocomplete: 'off',
      autocapitalize: 'characters',
      spellcheck: 'false',
      'aria-describedby': error ? 'code-help code-error' : 'code-help',
      'aria-invalid': error ? 'true' : 'false',
      disabled: busy ? '' : null,
    },
  });
  const alert = errorAlert(error);
  const button = primaryButton('Open my invitation', busy ? 'loading' : 'key', {
    disabled: busy || !code.trim() ? '' : null,
    'aria-busy': busy ? 'true' : 'false',
  });
  const help = element('p', {
    className: 'privacy-note',
    text: 'Your code is personal to you. Please do not share it. There are no prizes for helping somebody else complete their assessment.',
    attributes: { id: 'code-help' },
  });
  const form = element('form', {
    className: 'invite-form',
    attributes: { novalidate: '' },
  }, [label, input, alert, button, help]);

  const content = [heading, intro, form];
  const root = shell(
    viewFrame('key', content),
    busy ? 'Checking your invitation.' : '',
    {
      brandOptions: { title: 'Private access', subtitle: '' },
      footerText: '',
    },
  );
  return { root, form, input, alert, button };
}

function identitySentence(candidate) {
  const paragraph = element('p', { className: 'identity-line' });
  paragraph.append('This invitation was created for ');
  paragraph.append(element('strong', { text: candidate.displayName }));
  if (candidate.venueName) {
    paragraph.append(', General Manager at ');
    paragraph.append(element('strong', { text: candidate.venueName }));
  }
  paragraph.append('.');
  return paragraph;
}

export function confirmationView({ candidate, busy = false, error = '' }) {
  const heading = element('h1', {
    className: 'view-title',
    text: `Welcome, ${candidate.firstName}.`,
    attributes: { id: 'view-title', tabindex: '-1' },
  });
  const secondaryHeading = element('h2', {
    className: 'section-title',
    text: 'Your work has been noticed.',
  });
  const firstParagraph = element('p', {
    text: 'You have been invited because we believe you may be ready to take on more. This is not a promise of promotion, and it is not a corporate box-ticking exercise. It is a proper opportunity to show us how you think, what you stand for, where you are strong and where you may still need support.',
  });
  const secondParagraph = element('p', {
    text: 'We are not looking for polished perfection. We are looking for honesty, judgement, drive and the ability to take responsibility when things get difficult.',
  });
  const alert = errorAlert(error);
  const confirm = primaryButton("Yes, that's me", busy ? 'loading' : 'check', {
    type: 'button',
    'data-action': 'confirm',
    disabled: busy ? '' : null,
    'aria-busy': busy ? 'true' : 'false',
  });
  const reject = element('button', {
    className: 'button button--secondary',
    text: "This isn't me",
    attributes: {
      type: 'button',
      'data-action': 'reject',
      disabled: busy ? '' : null,
    },
  });
  const actions = element('div', { className: 'button-stack' }, [confirm, reject]);
  const copy = element('div', { className: 'leadership-copy' }, [
    secondaryHeading,
    firstParagraph,
    secondParagraph,
  ]);

  const root = shell(viewFrame('shield', [
    element('p', { className: 'eyebrow', text: 'PRIVATE LEADERSHIP INVITATION' }),
    heading,
    identitySentence(candidate),
    copy,
    alert,
    actions,
  ], 'confirmed'), busy ? 'Confirming your invitation.' : '');
  return { root, heading, confirm, reject, alert };
}

const NEXT_STEPS = [
  'Evidence review and consistent scoring.',
  'A short remote interview focused only on points that need testing or clarification.',
  'Acting Operations Manager trial for selected candidates.',
  'Formal appointment decision for 1 September.',
];

const ACTING_TRIAL_STAGES = [
  {
    days: 'Days 1-3',
    title: 'Take the brief',
    detail: 'Authority, patch, non-negotiable controls, reporting rhythm and handover.',
  },
  {
    days: 'Days 4-10',
    title: 'Establish the truth',
    detail: 'Venue visits, people picture, commercial baseline and immediate risk actions.',
  },
  {
    days: 'Days 11-20',
    title: 'Lead through GMs',
    detail: 'Clear venue plans, coaching, difficult conversations and disciplined follow-up.',
  },
  {
    days: 'Days 21-30',
    title: 'Prove repeatability',
    detail: 'Measure movement, show where ownership sits and present a concise patch review.',
  },
];

function submittedDetails(input) {
  const delivery = ['pending', 'sent', 'unknown'].includes(input?.delivery)
    ? input.delivery
    : 'pending';
  return typeof input === 'string'
    ? { firstName: input, delivery: 'pending', submittedAt: null }
    : {
        firstName: input?.firstName || 'there',
        delivery,
        submittedAt: input?.submittedAt || null,
      };
}

export function submissionDeliveryMessage(delivery) {
  if (delivery === 'sent') {
    return 'Your assessment is securely saved and the reviewer copy has been delivered.';
  }
  if (delivery === 'unknown') {
    return 'Your assessment is recorded as submitted and cannot be reopened. We are checking the reviewer copy; you do not need to submit again.';
  }
  return 'Your assessment is securely saved. The reviewer copy is still being delivered and will retry automatically; you do not need to submit again.';
}

export function updateSubmissionDeliveryView(root, delivery) {
  const notice = root?.querySelector?.('[data-delivery]');
  if (!notice || !['pending', 'sent', 'unknown'].includes(delivery)) return false;

  const scrollPosition = { x: window.scrollX, y: window.scrollY };
  notice.style.minHeight = `${Math.ceil(notice.getBoundingClientRect().height)}px`;
  notice.classList.remove(
    'submission-delivery--pending',
    'submission-delivery--sent',
    'submission-delivery--unknown',
  );
  notice.classList.add(`submission-delivery--${delivery}`);
  notice.dataset.delivery = delivery;
  const message = notice.querySelector('p');
  if (message) message.textContent = submissionDeliveryMessage(delivery);
  window.scrollTo(scrollPosition.x, scrollPosition.y);
  return true;
}

export function submittedView(input) {
  const details = submittedDetails(input);
  const heading = element('h1', {
    className: 'view-title view-title--status',
    text: `Thank you, ${details.firstName}. It is in.`,
    attributes: { id: 'view-title', tabindex: '-1' },
  });
  const body = element('p', {
    className: 'submission-intro',
    text: 'Your assessment has been saved and cannot be changed. We will review the evidence across every candidate before arranging short remote interviews. We are not looking for the person who wrote the most. We are looking for the person whose judgement, ownership and drive stand up when the detail is examined.',
  });
  const deliveryNotice = element('div', {
    className: `submission-delivery submission-delivery--${details.delivery}`,
    attributes: { role: 'status', 'data-delivery': details.delivery },
  }, [
    icon('confirmed'),
    element('p', { text: submissionDeliveryMessage(details.delivery) }),
  ]);
  const nextSteps = element('section', {
    className: 'submission-section',
    attributes: { 'aria-labelledby': 'submission-next-title' },
  }, [
    element('h2', {
      className: 'section-title',
      text: 'What happens next',
      attributes: { id: 'submission-next-title' },
    }),
    element('ol', { className: 'submission-steps' }, NEXT_STEPS.map((step) => (
      element('li', { className: 'submission-step' }, [
        element('span', { className: 'submission-step__text', text: step }),
      ])
    ))),
  ]);
  const trialPreview = element('section', {
    className: 'submission-section submission-trial',
    attributes: { 'aria-labelledby': 'submission-trial-title' },
  }, [
    element('h2', {
      className: 'section-title',
      text: 'A look at the 30-day acting trial',
      attributes: { id: 'submission-trial-title' },
    }),
    element('p', {
      className: 'submission-trial__intro',
      text: 'This is the framework selected candidates would work through. Seeing this preview does not mean you have been selected for the trial.',
    }),
    element('div', { className: 'submission-timeline' }, ACTING_TRIAL_STAGES.map((stage) => (
      element('article', { className: 'submission-stage' }, [
        element('p', { className: 'submission-stage__days', text: stage.days }),
        element('h3', { className: 'submission-stage__title', text: stage.title }),
        element('p', { className: 'submission-stage__detail', text: stage.detail }),
      ])
    ))),
    element('p', {
      className: 'submission-evidence',
      text: 'Trial evidence will examine safety and control, standards, people leadership, commercial movement, communication, delegation, pace and ownership.',
    }),
  ]);

  const root = shell(viewFrame('confirmed', [
      element('p', { className: 'eyebrow', text: 'ASSESSMENT RECEIVED' }),
      heading,
      body,
      deliveryNotice,
      nextSteps,
      trialPreview,
    ], 'confirmed'));
  root.classList.add('portal-shell--submission');
  root.querySelector('.portal-view')?.classList.add('portal-view--submission');

  return {
    root,
    heading,
    deliveryNotice,
  };
}

export function loadingView() {
  const heading = element('h1', {
    className: 'view-title view-title--status',
    text: 'Checking your invitation…',
    attributes: { id: 'view-title' },
  });
  return {
    root: shell(viewFrame('loading', [heading]), 'Checking your invitation.'),
  };
}
